import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import LuxuryEscapesSmallLogo from '../Header/Logo/logos/LuxuryEscapesSmallLogo'
import type { DevPanelData } from './types'
import Pane from 'components/Common/Pane'
import ContextsTab from './tabs/ContextsTab'
import ReduxTab from './tabs/ReduxTab'
import I18nTab from './tabs/I18nTab'
import { HorizontalLayout, Content, Sidebar, VerticalLayout, MenuItem } from './common'
import { useSessionPersistedTabs } from './hooks'
import AuthTab from './tabs/AuthTab'
import CustomerProfileTab from './tabs/CustomerProfileTab'

const RootLayout = styled.div`
  height: 100vh;
`

const Logo = styled(LuxuryEscapesSmallLogo)`
  height: ${rem(28)};
  width: ${rem(28)};
`

const tabs = [
  { id: 'contexts', component: ContextsTab },
  { id: 'redux', component: ReduxTab },
  { id: 'i18n', component: I18nTab },
  { id: 'auth', component: AuthTab },
  { id: 'customerProfile', component: CustomerProfileTab },
] as const

const tabIds = tabs.map(tab => tab.id)

const TAB_STORAGE_KEY = 'DevPanel:selectedTab'

interface Props {
  state: DevPanelData
}

function DevPanelRoot({ state }: Props) {
  const intl = useIntl()
  const [currentTabId, setTab] = useSessionPersistedTabs(tabIds, TAB_STORAGE_KEY)
  const tabLabels: Record<typeof tabIds[number], string> = {
    contexts: intl.formatMessage({
      id: 'temp_dev_panel_contexts_tab_label',
      defaultMessage: 'Contexts',
      description: 'Tab label for the contexts view in the customer portal developer tools panel',
    }),
    redux: intl.formatMessage({
      id: 'temp_dev_panel_redux_tab_label',
      defaultMessage: 'Redux state',
      description: 'Tab label for the Redux state view in the customer portal developer tools panel',
    }),
    i18n: intl.formatMessage({
      id: 'temp_dev_panel_i18n_tab_label',
      defaultMessage: 'i18n',
      description: 'Tab label for the internationalization view in the customer portal developer tools panel',
    }),
    auth: intl.formatMessage({
      id: 'temp_dev_panel_auth_tab_label',
      defaultMessage: 'Auth',
      description: 'Tab label for the authentication tools view in the customer portal developer tools panel',
    }),
    customerProfile: intl.formatMessage({
      id: 'temp_dev_panel_customer_profile_tab_label',
      defaultMessage: 'Customer Profile',
      description: 'Tab label for the customer profile overrides view in the customer portal developer tools panel',
    }),
  }

  const currentTab = tabs.find(tab => tab.id === currentTabId)!
  const TabContent = currentTab.component

  return <RootLayout>
    <VerticalLayout>
      <Pane padding={20}>
        <HeadingTextBlock variant="heading3" startIcon={<Logo />}>{intl.formatMessage({
          id: 'temp_dev_panel_heading',
          defaultMessage: 'Customer portal dev panel',
          description: 'Main heading for the customer portal developer tools panel',
        })}
        </HeadingTextBlock>
      </Pane>

      <Content>
        <HorizontalLayout>
          <Sidebar>
            {tabs.map(tab => (
              <MenuItem key={tab.id} onClick={setTab[tab.id]} title={tabLabels[tab.id]} selected={tab.id === currentTabId} />
            ))}
          </Sidebar>

          <Content>
            <TabContent state={state} />
          </Content>
        </HorizontalLayout>
      </Content>
    </VerticalLayout>
  </RootLayout>
}

export default DevPanelRoot
